/**
 * Utility to serialize data for Firebase by removing nested arrays and problematic structures
 */

/**
 * Recursively clean an object to be Firebase-compatible
 * - Converts nested arrays to objects with indexed keys
 * - Removes undefined values
 * - Handles circular references
 */
export function cleanForFirebase(obj: any, seen = new WeakSet()): any {
  // Handle primitives
  if (obj === null || obj === undefined) {
    return null;
  }
  
  if (typeof obj !== 'object') {
    return obj;
  }
  
  // Handle circular references
  if (seen.has(obj)) {
    return null;
  }
  seen.add(obj);
  
  // Handle arrays
  if (Array.isArray(obj)) {
    // Check if this is an array of arrays (nested array)
    const hasNestedArrays = obj.some(item => Array.isArray(item));
    
    if (hasNestedArrays) {
      // Convert to object with indexed keys
      const result: Record<string, any> = {};
      obj.forEach((item, index) => {
        result[index.toString()] = cleanForFirebase(item, seen);
      });
      return result;
    } else {
      // Regular array - clean each item
      return obj.map(item => cleanForFirebase(item, seen));
    }
  }
  
  // Handle objects
  const cleaned: Record<string, any> = {};
  
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      const value = obj[key];
      
      // Skip undefined values
      if (value === undefined) {
        continue;
      }
      
      // Skip functions
      if (typeof value === 'function') {
        continue;
      }
      
      // Recursively clean nested objects
      cleaned[key] = cleanForFirebase(value, seen);
    }
  }
  
  return cleaned;
}

/**
 * Serialize Fabric.js objects for Firebase storage
 * Removes problematic properties and nested arrays
 */
export function serializeFabricObjects(fabricObjects: any[]): any[] {
  return fabricObjects.map(obj => {
    // Convert to plain object first
    const plainObj = typeof obj.toObject === 'function' 
      ? obj.toObject(['strokeId', 'name']) 
      : obj;
    
    // Remove problematic properties that often contain nested arrays
    const {
      _objects,
      canvas,
      _element,
      filters,
      resizeFilter,
      _cacheCanvas,
      _cacheContext,
      __corner,
      oCoords,
      aCoords,
      matrixCache,
      ownMatrixCache,
      ...cleanObj
    } = plainObj;
    
    // Clean the remaining object
    return cleanForFirebase(cleanObj);
  });
}

/**
 * Deserialize objects back from Firebase format
 * Converts indexed objects back to arrays where appropriate
 */
export function deserializeFromFirebase(obj: any): any {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  
  // Check if this looks like an indexed object (should be converted back to array)
  const keys = Object.keys(obj);
  const isIndexedObject = keys.length > 0 && 
    keys.every(key => /^\d+$/.test(key)) &&
    keys.map(Number).sort((a, b) => a - b).every((val, idx) => val === idx);
  
  if (isIndexedObject) {
    // Convert back to array
    const arr = new Array(keys.length);
    keys.forEach(key => {
      arr[Number(key)] = deserializeFromFirebase(obj[key]);
    });
    return arr;
  }
  
  // Handle regular objects
  if (Array.isArray(obj)) {
    return obj.map(item => deserializeFromFirebase(item));
  }
  
  const result: Record<string, any> = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      result[key] = deserializeFromFirebase(obj[key]);
    }
  }
  
  return result;
}